import React, { useMemo, useRef, useState } from 'react';
import i18n from '../../../../i18n';
import { useTranslation } from 'react-i18next';
import { fmt, formatDrillUnit, formatMoney } from '../../data/metrics';
import { SEV_META } from '../../data/insights';
import { sourceMeta } from '../../data/reservationSources';
import type {
  DrillDownPerspective,
  DrillPanel,
  DriverDimension,
  InsightSeverity,
  RegimeDay,
} from '../../types';
import { Icon } from '../shared/Icon';
import { INSIGHTS_AGENT_NAME } from '../../lib/agentBranding';
import { useMaitreOptional } from '../maitre/MaitreContext';

export const PROV_LABEL: Record<string, string> = {
  get measured() {
    return i18n.t('performance:drillUi.prov.measured');
  },
  get inferred() {
    return i18n.t('performance:drillUi.prov.inferred');
  },
  get projected() {
    return i18n.t('performance:drillUi.prov.projected');
  },
  get estimated() {
    return i18n.t('performance:drillUi.prov.estimated');
  },
};

type RankingUnit = NonNullable<
  Extract<DrillPanel, { kind: 'ranking' }>['unit']
>;

// Formats a ranking row value by its declared unit so the same panel renders
// euros, counts, rates, minutes or turn multiples correctly across metrics.
export function fmtRankingValue(unit: RankingUnit | undefined, v: number): string {
  switch (unit) {
    case 'eur':
      return fmt.eur(v);
    case 'pct':
      return `${v}%`;
    case 'min':
      return `${Math.round(v)}m`;
    case 'x':
      return `${v}×`;
    case 'num':
    default:
      return fmt.num(v);
  }
}

// What "Ask maître d'AI" on a drill insight attaches as focus (docs/18).
export type NarrativeAgentContext = {
  metricKey?: string;
  outletId?: string;
};

/** Card-shaped shimmer while getOpportunityNarrative is in flight. */
export function OpportunityInsightSkeleton() {
  return (
    <div
      className="drill-insight drill-insight--opportunity drill-insight-skel"
      aria-busy="true"
      aria-live="polite"
      aria-label={i18n.t('performance:drillUi.loadingInsight')}
    >
      <div className="di-impact di-skel-impact" aria-hidden="true">
        <div className="di-skel-bar di-skel-bar--impact" />
        <div className="di-skel-bar di-skel-bar--impact-lab" />
      </div>
      <div className="di-head" aria-hidden="true">
        <span className="di-badge di-skel-badge" />
        <div className="di-copy">
          <div className="di-skel-bar di-skel-bar--kicker" />
          <div className="di-skel-bar di-skel-bar--title" />
          <div className="di-skel-bar di-skel-bar--title-2" />
        </div>
      </div>
      <div className="di-skel-why" aria-hidden="true">
        <div className="di-skel-bar" />
        <div className="di-skel-bar di-skel-bar--mid" />
        <div className="di-skel-bar di-skel-bar--short" />
      </div>
      <div className="di-skel-bar di-skel-bar--action" aria-hidden="true" />
      <div className="di-skel-foot" aria-hidden="true">
        <div className="di-skel-bar di-skel-bar--cta" />
      </div>
      <div className="di-skel-shine" aria-hidden="true" />
    </div>
  );
}

// Narrative panel reuses the insight-card chrome so server-built "what / why /
  // action" matches the catalog Finding on the drill (vs-own-history).
export function NarrativePanel({
  sev,
  title,
  why,
  action,
  impact,
  agentCtx,
}: {
  sev: InsightSeverity;
  title: string;
  why: string;
  action: string;
  impact?: { value: string; label: string } | null;
  agentCtx?: NarrativeAgentContext;
}) {
  const { t } = useTranslation('assistant');
  const meta = SEV_META[sev];
  const maitre = useMaitreOptional();

  // "Ask about this" always opens the panel: a new thread with the insight as
  // its focus, so the answer stays reachable after the drill closes.
  const askAbout = () => {
    maitre?.askAbout(
      {
        kind: 'insight',
        label: title,
        detail: i18n.t('performance:drillUi.askDetail', { why, action }),
        ...(agentCtx?.metricKey ? { metricKey: agentCtx.metricKey } : {}),
        ...(agentCtx?.outletId ? { outletId: agentCtx.outletId } : {}),
      },
      t('maitre.exploreInsight'),
    );
  };

  return (
    <div className={`drill-insight drill-insight--${sev}`}>
      {impact && (
        <div className="di-impact">
          <div className="di-impact-v" style={{ color: meta.c }}>
            {impact.value}
          </div>
          <div className="di-impact-l">{impact.label}</div>
        </div>
      )}
      <div className="di-head">
        <span className="di-badge" style={{ background: meta.c }}>
          <Icon name={meta.icon} size={16} fill={sev !== 'info'} />
        </span>
        <div className="di-copy">
          <div className="di-kicker" style={{ color: meta.c }}>
            {meta.label}
          </div>
          <div className="di-title">{title}</div>
        </div>
      </div>
      <div className="di-why">{why}</div>
      <div className="di-action">
        <Icon
          name="check"
          size={14}
          stroke={2.3}
          style={{ color: meta.c, marginTop: 1 }}
        />
        <span>{action}</span>
      </div>
      {maitre && (
        <div className="di-foot">
          <button type="button" className="di-ask-agent" onClick={askAbout}>
            <Icon name="bolt" size={14} />
            {t('ask.askName', { name: INSIGHTS_AGENT_NAME })}
          </button>
        </div>
      )}
    </div>
  );
}

export function SummaryCards({
  cards,
}: {
  cards: DrillDownPerspective[];
}) {
  return (
    <div className="drill-persp drill-cards">
      {cards.map(c => (
        <div className="dpsp-cell" key={c.label} title={c.hint}>
          <div className="dpsp-v">
            {c.value}
            {c.compareValue != null ? (
              <span className="dcrux-cmp-chip" title={i18n.t('performance:drillUi.vsComparePeriod')}>
                {c.compareValue}
                {c.compareDelta != null ? ` · ${c.compareDelta}` : ''}
              </span>
            ) : null}
          </div>
          <div className="dpsp-l">
            {c.label}
            {c.scope && (
              <span className={`kpi-scope scope-${c.scope}`}>
                {i18n.t(`metrics:scope.${c.scope}.label`, { defaultValue: c.scope })}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DriversPanel({
  title,
  dimensions,
  fmtVal,
}: {
  title: string;
  dimensions: DriverDimension[];
  fmtVal: (v: number) => string;
}) {
  return (
    <ImprovedDriversPanel title={title} dimensions={dimensions} fmtVal={fmtVal} />
  );
}

const PAIR_PRIMARY = 'var(--accent)';
/** Neutral second series; status colours stay on deltas. */
const PAIR_SECONDARY = 'var(--petrol-ink)';
/** Solid mute for non-top share bars — never a translucent tint. */
const ACCENT_MUTE = 'var(--st-quiet)';

/** Color key for dual-metric drill panels (Reservations + Rejected, etc.). */
export function PairMetricLegend({
  primaryLabel,
  secondaryLabel,
  primaryColor = PAIR_PRIMARY,
  secondaryColor = PAIR_SECONDARY,
}: {
  primaryLabel: string;
  secondaryLabel: string;
  primaryColor?: string;
  secondaryColor?: string;
}) {
  return (
    <div
      className="dp-pair-legend"
      aria-label={i18n.t('performance:drillUi.pairAria', { primary: primaryLabel, secondary: secondaryLabel })}
    >
      <span className="dp-pair-legend-item">
        <span className="dp-pair-swatch" style={{ background: primaryColor }} />
        {primaryLabel}
      </span>
      <span className="dp-pair-legend-item">
        <span
          className="dp-pair-swatch dp-pair-swatch-2"
          style={{ background: secondaryColor }}
        />
        {secondaryLabel}
      </span>
    </div>
  );
}

function dimTotal(dim: DriverDimension, which: 'primary' | 'secondary' = 'primary') {
  return dim.buckets.reduce(
    (s, b) => s + (which === 'primary' ? b.value : b.value2 ?? 0),
    0,
  );
}

function pairedTotalMeta(
  primaryFmt: string,
  secondaryFmt: string | null,
  primaryLabel?: string,
  secondaryLabel?: string,
): string {
  if (!secondaryFmt || !secondaryLabel) {
    return primaryLabel
      ? i18n.t('performance:drillUi.totalLabel', { value: primaryFmt, label: primaryLabel.toLowerCase() })
      : i18n.t('performance:drillUi.total', { value: primaryFmt });
  }
  const p = primaryLabel?.toLowerCase();
  const s = secondaryLabel.toLowerCase();
  return p
    ? i18n.t('performance:drillUi.totalPairLabel', {
        primary: primaryFmt,
        primaryLabel: p,
        secondary: secondaryFmt,
        secondaryLabel: s,
      })
    : i18n.t('performance:drillUi.totalPair', { primary: primaryFmt, secondary: secondaryFmt });
}

function sharePct(value: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((value / total) * 100);
}

function isMoneyUnit(unit?: string): boolean {
  if (!unit) return false;
  if (unit === 'eur' || unit === '€' || unit === '£' || unit === '$') return true;
  return /^[A-Z]{3}$/.test(unit);
}

function formatAvgDelta(dim: DriverDimension, diff: number): string {
  if (diff === 0) return dim.unit === 'min' ? '0m' : '0';
  const sign = diff > 0 ? '+' : '−';
  if (dim.unit === 'min') return `${sign}${Math.abs(diff)}m`;
  if (isMoneyUnit(dim.unit)) {
    // Prefix the sign so currency formatters never emit "€-12" / "CHF -12".
    return `${sign}${formatDrillUnit(Math.abs(diff), dim.unit)}`;
  }
  return `${sign}${Math.abs(diff)}`;
}

function driverBucketContext(
  dim: DriverDimension,
  bucket: { value: number; value2?: number },
  which: 'primary' | 'secondary' = 'primary',
): string {
  const value = which === 'primary' ? bucket.value : bucket.value2 ?? 0;
  const baseline = which === 'primary' ? dim.baseline : dim.baseline2 ?? 0;
  const total = dimTotal(dim, which);
  if (dim.mode === 'share' && total > 0) {
    const pct = sharePct(value, total);
    return pct == null ? '' : `${pct}%`;
  }
  if (dim.mode === 'rate') {
    const diff = +(value - baseline).toFixed(1);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return i18n.t('performance:drillUi.ptDelta', { delta: `${diff > 0 ? '+' : ''}${diff}` });
  }
  if (dim.mode === 'avg') {
    const diff = Math.round(value - baseline);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return formatAvgDelta(dim, diff);
  }
  return '';
}

function bucketDeltaLabel(dim: DriverDimension, value: number): string {
  if (dim.mode === 'rate') {
    const diff = +(value - dim.baseline).toFixed(1);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return i18n.t('performance:drillUi.vsAvg', { delta: i18n.t('performance:drillUi.ptDelta', { delta: `${diff > 0 ? '+' : ''}${diff}` }) });
  }
  if (dim.mode === 'avg') {
    const diff = Math.round(value - dim.baseline);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return i18n.t('performance:drillUi.vsAvg', { delta: formatAvgDelta(dim, diff) });
  }
  const pct = sharePct(value, dimTotal(dim));
  return pct == null ? '' : `${pct}%`;
}

function driverDimensionTakeaway(dim: DriverDimension): string | null {
  const top = dim.topDriver;
  const bottom = dim.bottomDriver;
  if (!top || !bottom || top.label === bottom.label) return null;
  if (dim.mode === 'share') {
    const topPct = bucketDeltaLabel(dim, top.value);
    const bottomPct = bucketDeltaLabel(dim, bottom.value);
    if (dim.shareOf === 'occupancy') {
      return i18n.t('performance:drillUi.takeawayOccupancy', {
        top: top.label,
        topCtx: topPct || i18n.t('performance:drillUi.most'),
        bottom: bottom.label,
        bottomCtx: bottomPct ? ` (${bottomPct})` : '',
      });
    }
    return i18n.t('performance:drillUi.takeawayVolume', {
      top: top.label,
      topCtx: topPct ? ` (${topPct})` : '',
      bottom: bottom.label,
      bottomCtx: bottomPct ? ` (${bottomPct})` : '',
    });
  }
  return i18n.t('performance:drillUi.takeawayRate', {
    top: top.label,
    topCtx: ` (${bucketDeltaLabel(dim, top.value)})`,
    bottom: bottom.label,
    bottomCtx: ` (${bucketDeltaLabel(dim, bottom.value)})`,
  });
}

function shareNoun(dims: DriverDimension[]): string {
  const explicit = dims.find(d => d.shareOf)?.shareOf;
  const unit = dims[0]?.unit;
  const noun = explicit || (isMoneyUnit(unit) ? 'revenue' : 'volume');
  return i18n.t(`performance:drillUi.shareNoun.${noun}`, { defaultValue: noun });
}

function driversPanelSubtitle(
  dims: DriverDimension[],
  primaryLabel?: string,
  secondaryLabel?: string,
): string {
  const n = dims.length;
  const modes = new Set(dims.map(d => d.mode));
  const shareOf = shareNoun(dims);
  const pairHint =
    primaryLabel && secondaryLabel
      ? i18n.t('performance:drillUi.pairHint', { primary: primaryLabel, secondary: secondaryLabel })
      : '';
  if (n === 1) {
    if (modes.has('share')) {
      return i18n.t('performance:drillUi.shareOfPair', { noun: shareOf, pair: pairHint });
    }
    if (modes.has('rate') || modes.has('avg')) {
      return i18n.t('performance:drillUi.differsByGroup', { pair: pairHint });
    }
    return i18n.t('performance:drillUi.breakdown');
  }
  if (modes.size === 1 && modes.has('share')) {
    return i18n.t('performance:drillUi.shareAcross', { noun: shareOf, count: n, pair: pairHint });
  }
  if (modes.size === 1 && (modes.has('rate') || modes.has('avg'))) {
    return i18n.t('performance:drillUi.differsAcross', { count: n, pair: pairHint });
  }
  return i18n.t('performance:drillUi.acrossBreakdowns', { count: n, pair: pairHint });
}

function sharedShareTotal(
  dims: DriverDimension[],
  fmt: (dim: DriverDimension, v: number) => string,
  fmt2?: (v: number) => string,
  primaryLabel?: string,
  secondaryLabel?: string,
): string | null {
  if (!dims.length || !dims.every(d => d.mode === 'share')) return null;
  const totals = dims.map(d => +dimTotal(d).toFixed(4));
  const first = totals[0];
  if (!totals.every(t => t === first)) return null;
  const primary = fmt(dims[0], dimTotal(dims[0]));
  const hasSecondary =
    !!fmt2 &&
    !!secondaryLabel &&
    dims.some(d => d.buckets.some(b => b.value2 != null));
  if (!hasSecondary) {
    return pairedTotalMeta(primary, null, primaryLabel, secondaryLabel);
  }
  const secondary = fmt2!(dimTotal(dims[0], 'secondary'));
  return pairedTotalMeta(primary, secondary, primaryLabel, secondaryLabel);
}

function rowBarTone(
  dim: DriverDimension,
  value: number,
  isTop: boolean,
  /** When dual-metric, keep one series color so the legend stays honest. */
  seriesColor?: string,
): string {
  if (seriesColor) return seriesColor;
  if (dim.mode === 'rate') {
    const diff = value - dim.baseline;
    const higherGood = dim.higherIsBetter === true;
    if (diff > 2) return higherGood ? 'var(--st-steady)' : 'var(--st-alert)';
    if (diff < -2) return higherGood ? 'var(--st-alert)' : 'var(--st-steady)';
  }
  return isTop ? 'var(--accent)' : ACCENT_MUTE;
}

export function ImprovedDriversPanel({
  title,
  dimensions,
  fmtVal,
  fmtVal2,
  primaryLabel,
  secondaryLabel,
  windowNote,
}: {
  title: string;
  dimensions: DriverDimension[];
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  /** Short name for the top bar (e.g. Reservations). */
  primaryLabel?: string;
  /** Short name for the secondary bar (e.g. Rejected). */
  secondaryLabel?: string;
  /** Set when a drivers panel uses a window other than the selected frame. */
  windowNote?: string;
}) {
  const deduped = useMemo(() => {
    const hasDay = dimensions.some(d => d.id === 'daypart');
    const hasRegime = dimensions.some(d => d.id === 'regime');
    if (hasDay && hasRegime) {
      return dimensions.filter(d => d.id !== 'regime');
    }
    return dimensions;
  }, [dimensions]);

  const dimFmt = (dim: DriverDimension, v: number) =>
    dim.unit ? formatDrillUnit(v, dim.unit) : fmtVal(v);

  const panelHasPair =
    !!fmtVal2 &&
    !!secondaryLabel &&
    deduped.some(d => d.buckets.some(b => b.value2 != null));
  const panelSubtitle = (() => {
    const rest = driversPanelSubtitle(
      deduped,
      panelHasPair ? primaryLabel : undefined,
      panelHasPair ? secondaryLabel : undefined,
    );
    return windowNote ? `${windowNote} · ${rest}` : rest;
  })();
  const sharedTotal = sharedShareTotal(
    deduped,
    dimFmt,
    panelHasPair ? fmtVal2 : undefined,
    panelHasPair ? primaryLabel : undefined,
    panelHasPair ? secondaryLabel : undefined,
  );
  const solo = deduped.length === 1;

  return (
    <div
      className={`drill-panel drill-drivers-panel${solo ? ' drill-drivers-solo-panel' : ''}`}
    >
      <div className="dp-head">
        <div className="dp-head-main">
          <h3>{title}</h3>
          <p className="dp-sub">{panelSubtitle}</p>
          {panelHasPair && primaryLabel && secondaryLabel ? (
            <PairMetricLegend
              primaryLabel={primaryLabel}
              secondaryLabel={secondaryLabel}
            />
          ) : null}
        </div>
        {sharedTotal ? <span className="dp-meta">{sharedTotal}</span> : null}
      </div>
      <div className={`drill-drivers${solo ? ' drill-drivers-solo' : ''}`}>
        {deduped.map(dim => {
          const maxV = Math.max(
            ...dim.buckets.map(b => Math.abs(b.value)),
            ...dim.buckets.map(b => Math.abs(b.value2 ?? 0)),
            0.0001,
          );
          const total = dimTotal(dim);
          const total2 = dimTotal(dim, 'secondary');
          const paired = dim.buckets.some(b => b.value2 != null) && !!fmtVal2;
          const insight = driverDimensionTakeaway(dim);
          const topLabel = dim.topDriver?.label;
          const bottomLabel = dim.bottomDriver?.label;
          const showDimTotal = dim.mode === 'share' && !sharedTotal;
          const showDimMeta = showDimTotal || dim.mode !== 'share';
          // Solo panels already name the dimension in the panel title.
          const showDimLabel = !solo;
          const showDimHead = showDimLabel || showDimMeta;
          const dimTotalLabel =
            dim.mode === 'share'
              ? pairedTotalMeta(
                  dimFmt(dim, total),
                  paired ? fmtVal2!(total2) : null,
                  panelHasPair ? primaryLabel : undefined,
                  panelHasPair ? secondaryLabel : undefined,
                )
              : `${i18n.t('performance:drillUi.avgValue', { value: dimFmt(dim, dim.baseline) })}${
                  paired && dim.baseline2 != null
                    ? ` · ${fmtVal2!(dim.baseline2)}${
                        panelHasPair && secondaryLabel
                          ? ` ${secondaryLabel.toLowerCase()}`
                          : ''
                      }`
                    : ''
                }`;
          return (
            <div className={`ddim${solo ? ' ddim-solo' : ''}`} key={dim.id}>
              {showDimHead ? (
                <div
                  className={`ddim-head${showDimLabel ? '' : ' ddim-head-meta-only'}`}
                >
                  {showDimLabel ? (
                    <span className="ddim-label">{dim.label}</span>
                  ) : null}
                  {showDimMeta ? (
                    <span className="ddim-base">{dimTotalLabel}</span>
                  ) : null}
                </div>
              ) : null}
              {insight ? (
                <div className="ddim-insight">
                  <span className="ddim-insight-kicker">{i18n.t('performance:drillUi.takeaway')}</span>
                  <span className="ddim-insight-text">{insight}</span>
                </div>
              ) : null}
              <div className="ddim-bars">
                {dim.buckets.map(b => {
                  const barW = Math.max(3, (Math.abs(b.value) / maxV) * 100);
                  const barW2 =
                    b.value2 != null
                      ? Math.max(3, (Math.abs(b.value2) / maxV) * 100)
                      : null;
                  const ctx = driverBucketContext(dim, b, 'primary');
                  const ctx2 = paired ? driverBucketContext(dim, b, 'secondary') : '';
                  const isTop = !!topLabel && b.label === topLabel;
                  const isMuted =
                    (!!bottomLabel && b.label === bottomLabel && !isTop) ||
                    (dim.mode === 'share' &&
                      !isTop &&
                      (sharePct(b.value, total) ?? 100) < 8);
                  // Paired panels: every primary bar uses the legend color.
                  // Top/bottom emphasis stays on label weight, not bar hue.
                  const tone = rowBarTone(
                    dim,
                    b.value,
                    isTop,
                    panelHasPair ? PAIR_PRIMARY : undefined,
                  );
                  const pctTone = panelHasPair
                    ? undefined
                    : tone;
                  return (
                    <div
                      className={`ddim-row${paired ? ' ddim-row-paired' : ''}${panelHasPair ? ' ddim-row-series' : ''}${isTop ? ' is-top' : ''}${isMuted ? ' is-muted' : ''}`}
                      key={b.label}
                      title={`${b.label}: ${dimFmt(dim, b.value)}${
                        panelHasPair && primaryLabel
                          ? ` ${primaryLabel.toLowerCase()}`
                          : ''
                      }${
                        b.value2 != null && fmtVal2
                          ? ` / ${fmtVal2(b.value2)}${
                              panelHasPair && secondaryLabel
                                ? ` ${secondaryLabel.toLowerCase()}`
                                : ''
                            }`
                          : ''
                      } · ${i18n.t('performance:drillUi.reservationsCount', { count: b.sample, formatted: fmt.num(b.sample) })}`}
                    >
                      <span className="ddim-name">{b.label}</span>
                      <div className="ddim-bar-stack">
                        <span className="ddim-track">
                          <span
                            className="ddim-fill"
                            style={{ width: `${barW}%`, background: tone }}
                          />
                        </span>
                        {barW2 != null && (
                          <span className="ddim-track ddim-track-2">
                            <span
                              className="ddim-fill ddim-fill-2"
                              style={{
                                width: `${barW2}%`,
                                background: PAIR_SECONDARY,
                              }}
                            />
                          </span>
                        )}
                      </div>
                      <div className="ddim-vals">
                        <span className="ddim-valgrp">
                          <span className="ddim-val">{dimFmt(dim, b.value)}</span>
                          {ctx ? (
                            <span
                              className="ddim-pct"
                              style={pctTone ? { color: pctTone } : undefined}
                            >
                              {ctx}
                            </span>
                          ) : null}
                        </span>
                        {paired && b.value2 != null && fmtVal2 && (
                          <span className="ddim-valgrp ddim-valgrp-2">
                            <span className="ddim-val ddim-val-2">
                              {fmtVal2(b.value2)}
                            </span>
                            {ctx2 ? (
                              <span className="ddim-pct ddim-pct-2">{ctx2}</span>
                            ) : null}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RankingPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'ranking' }>;
}) {
  const max = Math.max(...panel.rows.map(r => r.value), 0.0001);
  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        {panel.provenance && (
          <span className="prov-note">{PROV_LABEL[panel.provenance]}</span>
        )}
      </div>
      <div className="si-list">
        {panel.rows.map(r => (
          <div className="si-row" key={r.id}>
            <div className="si-name">
              <b>{r.name}</b>
              {r.sub && <span>{r.sub}</span>}
            </div>
            <div className="si-track">
              <span
                className="si-fill"
                style={{ width: `${(r.value / max) * 100}%` }}
              />
            </div>
            <div className="si-val">{fmtRankingValue(panel.unit, r.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

type PosItemsPanelT = Extract<DrillPanel, { kind: 'posItems' }>;
type PosSortKey = 'revenue' | 'qty' | 'avgPrice' | 'name';
type PosLimit = 10 | 25 | 50 | 100;

const POS_LIMITS: PosLimit[] = [10, 25, 50, 100];

/** Searchable / sortable POS article ranking — fields Hevo exposes (name, qty, revenue). */
export function PosItemsPanel({ panel }: { panel: PosItemsPanelT }) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<PosSortKey>('revenue');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [limit, setLimit] = useState<PosLimit>(10);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = panel.items;
    if (q) {
      rows = rows.filter(it => it.name.toLowerCase().includes(q));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
      if (sortKey === 'name') {
        return a.name.localeCompare(b.name) * dir;
      }
      return (a[sortKey] - b[sortKey]) * dir;
    });
    return sorted;
  }, [panel.items, query, sortKey, sortDir]);

  const visible = filtered.slice(0, limit);
  const total = Math.max(panel.totalRevenue, 0.0001);
  const barMax = Math.max(...visible.map(r => r.revenue), 0.0001);
  const unit = panel.unit || visible[0]?.currency || '€';

  return (
    <div className="drill-panel pos-items-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        <span className="prov-note">
          {i18n.t('performance:drillUi.itemsOf', { shown: filtered.length, total: panel.items.length })}
          {panel.provenance ? ` · ${PROV_LABEL[panel.provenance]}` : ''}
        </span>
      </div>
      <div className="pos-items-toolbar">
        <input
          type="search"
          className="pos-items-search"
          placeholder={i18n.t('performance:drillUi.searchItems')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label={i18n.t('performance:drillUi.searchPosItems')}
        />
        <label className="pos-items-ctrl">
          <span>{i18n.t('performance:drillUi.sort')}</span>
          <select
            value={sortKey}
            onChange={e => {
              const next = e.target.value as PosSortKey;
              setSortKey(next);
              setSortDir(next === 'name' ? 'asc' : 'desc');
            }}
          >
            <option value="revenue">{i18n.t('performance:drillUi.sortRevenue')}</option>
            <option value="qty">{i18n.t('performance:drillUi.sortQty')}</option>
            <option value="avgPrice">{i18n.t('performance:drillUi.sortAvgPrice')}</option>
            <option value="name">{i18n.t('performance:drillUi.sortName')}</option>
          </select>
        </label>
        <button
          type="button"
          className="pos-items-dir"
          onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
          aria-label={sortDir === 'asc' ? i18n.t('performance:drillUi.ascending') : i18n.t('performance:drillUi.descending')}
          title={sortDir === 'asc' ? i18n.t('performance:drillUi.ascending') : i18n.t('performance:drillUi.descending')}
        >
          {sortDir === 'asc' ? '↑' : '↓'}
        </button>
        <label className="pos-items-ctrl">
          <span>{i18n.t('performance:drillUi.show')}</span>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value) as PosLimit)}
          >
            {POS_LIMITS.map(n => (
              <option key={n} value={n}>
                {n === 100 ? i18n.t('performance:drillUi.all') : n}
              </option>
            ))}
          </select>
        </label>
      </div>
      {visible.length === 0 ? (
        <div className="si-empty">{i18n.t('performance:drillUi.noItemsMatch')}</div>
      ) : (
        <div className="si-list pos-items-list">
          {visible.map(r => {
            const share = (r.revenue / total) * 100;
            return (
              <div className="si-row pos-items-row" key={r.id}>
                <div className="si-name">
                  <b>{r.name}</b>
                  <span>
                    {i18n.t('performance:drillUi.soldAvg', {
                      qty: fmt.num(r.qty),
                      price: formatMoney(r.avgPrice, r.currency || unit),
                    })}
                    {` · ${share.toFixed(share >= 10 ? 0 : 1)}%`}
                  </span>
                </div>
                <div className="si-track">
                  <span
                    className="si-fill"
                    style={{ width: `${(r.revenue / barMax) * 100}%` }}
                  />
                </div>
                <div className="si-val">
                  {formatMoney(r.revenue, r.currency || unit)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {filtered.length > visible.length && (
        <button
          type="button"
          className="pos-items-more"
          onClick={() => {
            const next = POS_LIMITS.find(n => n > limit) ?? 100;
            setLimit(next);
          }}
        >
          {i18n.t('performance:drillUi.showMoreLeft', { count: filtered.length - visible.length })}
        </button>
      )}
    </div>
  );
}

type BySourceUnit = NonNullable<
  Extract<DrillPanel, { kind: 'bySource' }>['unit']
>;

function fmtSourceValue(unit: BySourceUnit | undefined, v: number): string {
  switch (unit) {
    case 'eur':
      return fmt.eur(v);
    case 'pct':
      return `${v}%`;
    case 'min':
      return `${Math.round(v)}m`;
    case 'x':
      return `${v}×`;
    case 'num':
    default:
      return fmt.num(v);
  }
}

// "By source" comparison: per-source value bars for the active metric, plus the
// guests share and (Performance only) the guests delta vs the prior period.
export function BySourcePanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'bySource' }>;
}) {
  const max = Math.max(...panel.rows.map(r => Math.abs(r.value)), 0.0001);
  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        {panel.provenance && (
          <span className="prov-note">{PROV_LABEL[panel.provenance]}</span>
        )}
      </div>
      <div className="si-list">
        {panel.rows.map(r => {
          const m = sourceMeta(r.source);
          return (
            <div className="si-row" key={r.source}>
              <div className="si-name">
                <b>
                  <span
                    className="srcmix-dot"
                    style={{ background: m.color, marginRight: 7 }}
                  />
                  {m.label}
                </b>
                <span>
                  {i18n.t('performance:drillUi.pctOfGuests', { pct: r.share })}
                  {r.deltaPct != null
                    ? ` · ${i18n.t('performance:drillUi.vsPrior', {
                        delta: `${r.deltaPct >= 0 ? '+' : ''}${r.deltaPct}`,
                      })}`
                    : ''}
                </span>
              </div>
              <div className="si-track">
                <span
                  className="si-fill"
                  style={{
                    width: `${Math.max(2, (Math.abs(r.value) / max) * 100)}%`,
                    background: m.color,
                  }}
                />
              </div>
              <div className="si-val">{fmtSourceValue(panel.unit, r.value)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function minutePct(minute: number, axisStart: number, axisEnd: number): number {
  const span = Math.max(axisEnd - axisStart, 1);
  return ((minute - axisStart) / span) * 100;
}

function occupancyChartMaxY(values: number[]): number {
  const peak = Math.max(...values, 1);
  const padded = Math.ceil((peak * 1.3) / 5) * 5;
  return Math.min(100, Math.max(15, padded));
}

function occupancyOpenRuns(
  coords: { x: number; y: number; v: number | null }[],
): { x: number; y: number; v: number }[][] {
  const runs: { x: number; y: number; v: number }[][] = [];
  let cur: { x: number; y: number; v: number }[] = [];
  for (const c of coords) {
    if (c.v == null) {
      if (cur.length) {
        runs.push(cur);
        cur = [];
      }
    } else {
      cur.push({ x: c.x, y: c.y, v: c.v });
    }
  }
  if (cur.length) runs.push(cur);
  return runs;
}

function buildSmoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  if (pts.length < 2) return '';
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const cx = (prev.x + cur.x) / 2;
    d += ` Q${cx},${prev.y} ${cur.x},${cur.y}`;
  }
  return d;
}

function formatClockMinute(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function occupancyYLabels(maxY: number, unit: '%' | '' = '%'): string[] {
  return [1, 0.75, 0.5, 0.25, 0].map(f => `${Math.round(maxY * f)}${unit}`);
}

/** Whole-hour clock ticks every `stepH` hours inside the axis (11:00, 13:00…). */
export function clockHourTicks(axisStart: number, axisEnd: number, stepH = 2): number[] {
  const first = Math.ceil(axisStart / 60) * 60;
  const ticks: number[] = [];
  for (let m = first; m <= axisEnd; m += stepH * 60) ticks.push(m);
  return ticks;
}

/** Shared y-axis top for rhythm charts drawn in seats: the most seats on offer (or taken). */
export function rhythmSeatMaxY(days: Pick<RegimeDay, 'seatsSeries' | 'capacitySeries'>[]): number {
  const peak = Math.max(
    1,
    ...days.flatMap(d => [...(d.capacitySeries ?? []), ...(d.seatsSeries ?? [])]),
  );
  return Math.ceil((peak * 1.1) / 10) * 10;
}

/** A rhythm day carries seats and capacity per slot: draw it in seats with the capacity line. */
function hasSeatSeries(day: RegimeDay): boolean {
  return (
    !!day.seatsSeries &&
    !!day.capacitySeries &&
    day.seatsSeries.length === day.series.length &&
    day.capacitySeries.length === day.series.length
  );
}

const OCC_CHART_W = 480;
const OCC_CHART_H = 120;
const OCC_PAD_X = 2;
const OCC_PAD_Y = 8;

/** HTML marker — stays round even when the SVG stretches with preserveAspectRatio=none. */
function OccMarker({
  xPct,
  yPct,
  kind,
}: {
  xPct: number;
  yPct: number;
  kind: 'peak' | 'hover';
}) {
  return (
    <span
      className={`regime-marker regime-marker-${kind}`}
      style={{ left: `${xPct}%`, top: `${yPct}%` }}
      aria-hidden
    />
  );
}

export function OccupancyDayChart({
  day,
  maxY,
  axisStart,
  axisEnd,
  slotMinutes,
  hourTicks,
}: {
  day: RegimeDay;
  maxY: number;
  axisStart: number;
  axisEnd: number;
  slotMinutes: number;
  hourTicks: number[];
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{
    xPct: number;
    yPct: number;
    value: number | null;
    minute: number;
  } | null>(null);

  const seatMode = hasSeatSeries(day);
  const capacity = seatMode ? day.capacitySeries! : null;
  // Seats mode: a slot is drawn when it is open or guests are still seated.
  const values = seatMode
    ? day.seatsSeries!.map((v, i) => (capacity![i] > 0 || v > 0 ? v : null))
    : day.series.map(v => (v == null ? null : v));
  const numeric = values.filter((v): v is number => v != null);
  if (!numeric.length) return null;
  const n = values.length;
  let peakI = 0;
  let peakV = -1;
  values.forEach((v, i) => {
    if (v != null && v > peakV) {
      peakV = v;
      peakI = i;
    }
  });
  const coords = values.map((v, i) => {
    const x = OCC_PAD_X + (i / Math.max(n - 1, 1)) * (OCC_CHART_W - OCC_PAD_X * 2);
    const y =
      v == null
        ? OCC_CHART_H
        : OCC_CHART_H - OCC_PAD_Y - (v / maxY) * (OCC_CHART_H - OCC_PAD_Y * 2);
    return { x, y, v };
  });
  const openCoords = coords.filter(c => c.v != null);
  if (!openCoords.length) return null;
  const runs = occupancyOpenRuns(coords);
  const peak = coords[peakI];
  const gridSteps = 4;
  const yLabels = occupancyYLabels(maxY, seatMode ? '' : '%');
  const yOf = (v: number) =>
    OCC_CHART_H - OCC_PAD_Y - (Math.min(v, maxY) / maxY) * (OCC_CHART_H - OCC_PAD_Y * 2);
  // Seats on offer as a step line: one flat segment per slot.
  const capacityPath = capacity
    ? capacity
        .map((c, i) => {
          const x0 = coords[i].x;
          const x1 = i + 1 < n ? coords[i + 1].x : coords[i].x;
          const y = yOf(c);
          return `${i === 0 ? 'M' : 'L'}${x0},${y} L${x1},${y}`;
        })
        .join(' ')
    : null;
  const toXPct = (svgX: number) => (svgX / OCC_CHART_W) * 100;
  const toYPct = (svgY: number) => (svgY / OCC_CHART_H) * 100;

  const updateHover = (clientX: number) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / Math.max(rect.width, 1)),
    );
    const idx = Math.round(ratio * Math.max(n - 1, 0));
    const pt = coords[idx];
    setHover({
      xPct: toXPct(pt.x),
      yPct: toYPct(pt.y),
      value: pt.v,
      minute: axisStart + idx * slotMinutes,
    });
  };

  const showPeak = !hover && peak?.v != null;
  const showHover = hover?.value != null;

  return (
    <div className="regime-rhythm-day-chart">
      <div className="regime-rhythm-chart-row">
        <div className="regime-rhythm-y-axis" aria-hidden>
          {yLabels.map(lab => (
            <span key={lab}>{lab}</span>
          ))}
        </div>
        <div
          ref={wrapRef}
          className="regime-rhythm-chart-wrap"
          onMouseMove={e => updateHover(e.clientX)}
          onMouseLeave={() => setHover(null)}
        >
          <svg
            viewBox={`0 0 ${OCC_CHART_W} ${OCC_CHART_H}`}
            className="regime-rhythm-chart"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient
                id={`occ-fill-${day.dow}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.1" />
                <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
              </linearGradient>
            </defs>
            {Array.from({ length: gridSteps + 1 }, (_, i) => {
              const y =
                OCC_PAD_Y + ((OCC_CHART_H - OCC_PAD_Y * 2) / gridSteps) * i;
              return (
                <line
                  key={i}
                  x1={OCC_PAD_X}
                  y1={y}
                  x2={OCC_CHART_W - OCC_PAD_X}
                  y2={y}
                  className="regime-grid-line"
                />
              );
            })}
            {capacityPath && (
              <path d={capacityPath} className="regime-capacity-line" fill="none" />
            )}
            {runs.map((pts, i) => {
              const linePath = buildSmoothPath(pts);
              if (!linePath) return null;
              const first = pts[0];
              const last = pts[pts.length - 1];
              const area = `${linePath} L${last.x},${OCC_CHART_H - OCC_PAD_Y} L${first.x},${OCC_CHART_H - OCC_PAD_Y} Z`;
              return (
                <g key={i}>
                  <path d={area} fill={`url(#occ-fill-${day.dow})`} />
                  <path d={linePath} className="regime-area-line" fill="none" />
                </g>
              );
            })}
            {showPeak && (
              <line
                x1={peak.x}
                y1={peak.y}
                x2={peak.x}
                y2={OCC_CHART_H - OCC_PAD_Y}
                className="regime-peak-stem"
              />
            )}
            {hover && (
              <line
                x1={(hover.xPct / 100) * OCC_CHART_W}
                y1={OCC_PAD_Y}
                x2={(hover.xPct / 100) * OCC_CHART_W}
                y2={OCC_CHART_H - OCC_PAD_Y}
                className="regime-hover-stem"
              />
            )}
          </svg>
          {showPeak && peak && (
            <OccMarker
              xPct={toXPct(peak.x)}
              yPct={toYPct(peak.y)}
              kind="peak"
            />
          )}
          {showHover && hover && (
            <OccMarker xPct={hover.xPct} yPct={hover.yPct} kind="hover" />
          )}
          {hover && (
            <div
              className="regime-rhythm-tooltip"
              style={{
                left: `${hover.xPct}%`,
                transform:
                  hover.xPct > 82
                    ? 'translate(-100%, -100%)'
                    : hover.xPct < 18
                      ? 'translate(0, -100%)'
                      : 'translate(-50%, -100%)',
              }}
            >
              <strong>
                {hover.value == null
                  ? '-'
                  : seatMode
                    ? i18n.t('performance:drillUi.rhythmSeatsTip', {
                        seats: Math.round(hover.value),
                        capacity: Math.round(
                          capacity![
                            Math.round((hover.minute - axisStart) / slotMinutes)
                          ] ?? 0,
                        ),
                      })
                    : `${hover.value}%`}
              </strong>
              <span>{formatClockMinute(hover.minute)}</span>
            </div>
          )}
        </div>
      </div>
      {hourTicks.length > 0 && (
        <div className="regime-rhythm-axis">
          {hourTicks.map(m => (
            <span
              key={m}
              style={{ left: `${minutePct(m, axisStart, axisEnd)}%` }}
            >
              {formatClockMinute(m)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Per-weekday occupancy rhythm: one self-contained chart per day.
export function RegimeGridPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'regimeGrid' }>;
}) {
  const activeDays = panel.days.filter(d => d.operatingDays > 0);
  const allValues = activeDays.flatMap(d =>
    d.series.filter((v): v is number => v != null),
  );
  const chartMaxY = activeDays.some(hasSeatSeries)
    ? rhythmSeatMaxY(activeDays)
    : occupancyChartMaxY(allValues.length ? allValues : [0]);
  const hourTicks = clockHourTicks(panel.axisStart, panel.axisEnd);

  return (
    <div className="drill-panel regime-panel regime-rhythm-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        <span className="regime-basis">
          {i18n.t('performance:drillUi.slotFillVs', {
            minutes: panel.slotMinutes,
            basis: panel.basisLabel.toLowerCase(),
          })}
        </span>
      </div>
      {panel.subtitle && <p className="regime-sub">{panel.subtitle}</p>}
      <div className="regime-rhythm-days">
        {activeDays.map(d => (
          <div className="regime-rhythm-day" key={d.dow}>
            <div className="regime-rhythm-head">
              <span className="regime-rhythm-dow">{d.label}</span>
              <span className="regime-rhythm-avg">
                <b>{d.avgPct}%</b>
                <span className="regime-rhythm-avg-label">{i18n.t('performance:drillUi.busyAvg')}</span>
              </span>
              <span className="regime-rhythm-meta">
                {i18n.t('performance:drillUi.peakAt', { pct: d.peakPct, label: d.peakLabel })}
              </span>
            </div>
            <OccupancyDayChart
              day={d}
              maxY={chartMaxY}
              axisStart={panel.axisStart}
              axisEnd={panel.axisEnd}
              slotMinutes={panel.slotMinutes}
              hourTicks={hourTicks}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function BrainSeatingPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'brainSeating' }>;
}) {
  const { t } = useTranslation('performance');
  const coversShare = panel.covers
    ? Math.round((panel.autoCovers / panel.covers) * 100)
    : 0;
  // Seat-in origin is unavailable in BigQuery — hide the column when empty
  // so fleet/outlet drills don't show a header with nothing under it.
  const showShift = panel.byShift != null;
  const showOrigin = panel.byOrigin.length > 0;
  const showCols = showShift || showOrigin;
  const maxShift =
    showShift && panel.byShift!.length
      ? Math.max(...panel.byShift!.map(s => s.autoPct), 0.0001)
      : 1;
  const maxOrigin = showOrigin
    ? Math.max(...panel.byOrigin.map(o => o.autoSeated), 0.0001)
    : 1;
  const singleCol = showCols && !(showShift && showOrigin);
  return (
    <div className="drill-panel brain-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        <span className="prov-note">{PROV_LABEL.measured}</span>
      </div>
      <div className="brain-summary">
        <div className="brain-stat brain-stat-primary">
          <div className="brain-stat-v">{panel.autoPct}%</div>
          <div className="brain-stat-l">{i18n.t('performance:drillUi.autoSeatedByBrain')}</div>
        </div>
        <div className="brain-stat">
          <div className="brain-stat-v">
            {fmt.num(panel.autoSeated)} / {fmt.num(panel.seatable)}
          </div>
          <div className="brain-stat-l">{t('drillUi.brainSeatableBase')}</div>
        </div>
        <div className="brain-stat">
          <div className="brain-stat-v">{coversShare}%</div>
          <div className="brain-stat-l">{i18n.t('performance:drillUi.guestsOnAuto')}</div>
        </div>
        <div className="brain-stat">
          <div className="brain-stat-v">{panel.coveragePct}%</div>
          <div className="brain-stat-l">{i18n.t('performance:drillUi.flagCoverageLower')}</div>
        </div>
      </div>
      {showCols && (
        <div className={`brain-cols${singleCol ? ' brain-cols-single' : ''}`}>
          {showShift && (
            <div className="brain-col">
              <h4>{i18n.t('performance:drillUi.byShift')}</h4>
              {panel.byShift!.length ? (
                panel.byShift!.map(s => (
                  <div className="si-row" key={s.shiftId}>
                    <div className="si-name">
                      <b>{s.label}</b>
                      <span className="brain-sub">
                        {fmt.num(s.autoSeated)} / {fmt.num(s.seatable ?? s.booked)} · n≥3
                      </span>
                    </div>
                    <div className="si-track">
                      <span
                        className="si-fill brain-fill"
                        style={{ width: `${(s.autoPct / maxShift) * 100}%` }}
                      />
                    </div>
                    <div className="si-val">{s.autoPct}%</div>
                  </div>
                ))
              ) : (
                <p className="regime-note">
                  {i18n.t('performance:drillUi.noShiftsMin', { count: 3 })}
                </p>
              )}
            </div>
          )}
          {showOrigin && (
            <div className="brain-col">
              <h4>{i18n.t('performance:drillUi.bySeatInOrigin')}</h4>
              {panel.byOrigin.map(o => (
                <div className="si-row" key={o.origin}>
                  <div className="si-name">
                    <b>{o.label}</b>
                    <span className="brain-sub">
                      {i18n.t('performance:drillUi.autoCount', { count: fmt.num(o.autoSeated), pct: o.autoPct })}
                    </span>
                  </div>
                  <div className="si-track">
                    <span
                      className="si-fill brain-fill"
                      style={{ width: `${(o.autoSeated / maxOrigin) * 100}%` }}
                    />
                  </div>
                  <div className="si-val">{fmt.num(o.autoSeated)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Blocked-times breakdown: estimated lost seat-hours, the closure types and
// their day-of-week distribution. Replaces the old "Blocked times" count tile.
export function BlockedImpactPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'blockedImpact' }>;
}) {
  const maxType = Math.max(...panel.byType.map(t => t.count), 0.0001);
  const maxDow = Math.max(...panel.byDow.map(d => d.events), 0.0001);
  const blockedPct = Number.isFinite(panel.blockedPct) ? panel.blockedPct : 0;
  const available = Number.isFinite(panel.availableSeatHours)
    ? panel.availableSeatHours
    : 0;
  return (
    <div className="drill-panel blocked-panel">
      <div className="dp-head">
        <h3>{panel.title}</h3>
        <span className="prov-note">{PROV_LABEL.estimated}</span>
      </div>
      <div className="blocked-summary">
        <div className="blocked-stat blocked-stat-primary">
          <div className="blocked-stat-v">{blockedPct}%</div>
          <div className="blocked-stat-l">{i18n.t('performance:drillUi.blockedOfAvailablePct')}</div>
        </div>
        <div className="blocked-stat">
          <div className="blocked-stat-v">{fmt.num(panel.seatHours)}</div>
          <div className="blocked-stat-l">
            {i18n.t('performance:drillUi.blockedOfAvailable', { available: fmt.num(available) })}
          </div>
        </div>
        <div className="blocked-stat">
          <div className="blocked-stat-v">{fmt.num(panel.totalEvents)}</div>
          <div className="blocked-stat-l">{i18n.t('performance:drillUi.closures')}</div>
        </div>
      </div>
      <div className="blocked-cols">
        <div className="si-list">
          {panel.byType.map(t => (
            <div className="si-row" key={t.label}>
              <div className="si-name">
                <b>{t.label}</b>
              </div>
              <div className="si-track">
                <span
                  className="si-fill"
                  style={{ width: `${(t.count / maxType) * 100}%` }}
                />
              </div>
              <div className="si-val">{fmt.num(t.count)}</div>
            </div>
          ))}
        </div>
        <div className="blocked-dow">
          {panel.byDow.map(d => (
            <div className="blocked-dow-col" key={d.dow} title={`${d.label}: ${d.events}`}>
              <span className="blocked-dow-bar-wrap">
                <span
                  className="blocked-dow-bar"
                  style={{ height: `${Math.max(2, (d.events / maxDow) * 100)}%` }}
                />
              </span>
              <span className="blocked-dow-label">{d.label[0]}</span>
            </div>
          ))}
        </div>
      </div>
      {panel.note && <p className="regime-note">{panel.note}</p>}
    </div>
  );
}

// Shared switch that renders the metric-specific panel list for both the Live
// and Performance drill-downs. `fmtVal` formats driver/metric values per the
// active metric.
function SettingsRecommendationsPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'settingsRecommendations' }>;
}) {
  return (
    <div className="drill-panel">
      <h3 className="drill-panel-title">{panel.title}</h3>
      <div className="sm-issues">
        {panel.issues.map(g => {
          const sev = SEV_META[g.severity] ?? SEV_META.info;
          return (
            <div key={g.id} className="ins-card sm-issue">
              <div className="ins-head">
                <span className="ins-dot" style={{ background: sev.c }} />
                <strong>{g.title}</strong>
              </div>
              <div className="sm-issue-impact">{g.impact}</div>
              <div className="ins-action">
                <Icon name="check" size={12} />
                <span>{g.recommendation}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanPanel({
  panel,
}: {
  panel: Extract<DrillPanel, { kind: 'plan' }>;
}) {
  return (
    <div className="drill-panel ns-plan-panel">
      <h3 className="drill-panel-title">{panel.title}</h3>
      <ol className="ns-plan-list">
        {panel.items.map(item => (
          <li key={item.priority} className="ns-plan-item">
            <div className="ns-plan-item-head">
              <span className="ns-plan-priority">{item.priority}</span>
              <strong>{item.title}</strong>
              <span className="ns-plan-cat">{item.category}</span>
            </div>
            <p className="ns-plan-why">{item.why}</p>
            <p className="ns-plan-action">{item.action}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function DrillPanels({
  panels,
  fmtVal,
  agentCtx,
}: {
  panels: DrillPanel[];
  fmtVal: (v: number) => string;
  agentCtx?: NarrativeAgentContext;
}) {
  return (
    <>
      {panels.map((panel, i) => {
        if (panel.kind === 'narrative') {
          return (
            <NarrativePanel
              key={`p${i}`}
              sev={panel.sev}
              title={panel.title}
              why={panel.why}
              action={panel.action}
              impact={panel.impact}
              agentCtx={agentCtx}
            />
          );
        }
        if (panel.kind === 'summaryCards') {
          return <SummaryCards key={`p${i}`} cards={panel.cards} />;
        }
        if (panel.kind === 'ranking') {
          return <RankingPanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'posItems') {
          return <PosItemsPanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'bySource') {
          return <BySourcePanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'regimeGrid') {
          return <RegimeGridPanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'blockedImpact') {
          return <BlockedImpactPanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'brainSeating') {
          return <BrainSeatingPanel key={`p${i}`} panel={panel} />;
        }
        if (panel.kind === 'drivers') {
          return (
            <DriversPanel
              key={`p${i}`}
              title={panel.title}
              dimensions={panel.dimensions}
              fmtVal={fmtVal}
            />
          );
        }
        if (panel.kind === 'settingsRecommendations') {
          return (
            <SettingsRecommendationsPanel key={`p${i}`} panel={panel} />
          );
        }
        if (panel.kind === 'plan') {
          return <PlanPanel key={`p${i}`} panel={panel} />;
        }
        return null;
      })}
    </>
  );
}
